import { t } from 'i18next'
import { type FC, useMemo } from 'react'
import type { EnhancedMarketId } from '#api/enhancements/enhancedTmsApiTypes'
import { useGetMarketsQuery } from '#api/tmsApiHooks'
import { Icon } from '#components/Icon'
import { Select, type SelectEntry } from '#components/Select'
import { Spinner } from '#components/Spinner'
import WarningIcon from '#icons/warning-triangle-outline-16.svg'
import { SelectionField } from './SelectionField'
import { TextInput } from './TextInput'

export const MarketInput: FC<{
  marketId?: EnhancedMarketId
  onSelected: (marketId: EnhancedMarketId) => void
  kind?: 'inline' | 'full'
  disabled?: boolean
}> = ({ marketId, onSelected, kind, disabled }) => {
  const markets = useGetMarketsQuery()
  const marketSelectItems = useMemo(() => {
    return (markets.data?.markets ?? [])
      .map((m) => {
        return {
          id: m.marketId ?? ('unknown' as unknown as EnhancedMarketId),
          value: `${m.marketName ?? t('setup.market.unknown')} (${m.marketId})`,
        } satisfies SelectEntry<EnhancedMarketId, string>
      })
      .sort((a, b) => a.value.localeCompare(b.value))
  }, [markets.data])
  const foundMarket = markets.data?.markets?.find((m) => m.marketId === marketId)

  const input = useMemo(() => {
    if (markets.isLoading) {
      return (
        <section className="split-flex">
          <Spinner size="s" />
          <span>{t('setup.market.loading', 'Loading markets…')}</span>
        </section>
      )
    }
    if (!markets.isSuccess) {
      return (
        <section className="has-error">
          <Icon iconUrl={WarningIcon} />
          <span>{t('setup.market.error.failed-loading-markets', 'Failed loading markets')}</span>
        </section>
      )
    }

    return (
      <Select
        label={kind === 'inline' ? undefined : t('setup.market.label', 'Market')}
        required
        slim
        disabled={disabled}
        options={marketSelectItems}
        value={marketId}
        placeholder={foundMarket?.marketName ?? t('setup.market.unknown', 'Unknown Market')}
        onChange={(item) => {
          if (item) {
            onSelected(item)
          }
        }}
      />
    )
  }, [markets.isSuccess, markets.isLoading, marketSelectItems, marketId, onSelected, foundMarket?.marketName, kind, disabled])

  return (
    <SelectionField>
      {input}

      <TextInput
        type="number"
        value={marketId}
        disabled={disabled}
        onChange={(x) => {
          if (!Number.isNaN(x.target.valueAsNumber)) {
            onSelected(x.target.valueAsNumber as EnhancedMarketId)
          }
        }}
      />
    </SelectionField>
  )
}
